import Vue from "vue";
import vuetify from "./plugins/vuetify";
import VueWait from "vue-wait";
import VTooltip from "v-tooltip";
import VueTheMask from "vue-the-mask";
import VueClipboard from "vue-clipboard2";
import Vuelidate from "vuelidate";
import VModal from "vue-js-modal";
import Vuex from "vuex";
import router from "./router/routes";
import VueRouter from "vue-router";
import VueCurrencyFilter from "vue-currency-filter";
import { SpringSpinner } from "epic-spinners";
import App from "./App.vue";
import store from "./store/store";
import { timestampToDateString, timestampToFancyDate } from "./helpers/helpers";

/**  create fontawesome library */
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCaretDown,
  faChevronUp,
  faChevronDown,
  faChevronRight,
  faCheck,
  faCheckCircle,
  faTimes,
  faTimesCircle,
  faUserCircle,
  faPlusCircle,
  faExclamationTriangle,
  faExclamationCircle,
  faAngleLeft,
  faInfoCircle,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  faCcAmex,
  faCcDinersClub,
  faCcDiscover,
  faCcJcb,
  faCcMastercard,
  faCcVisa,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// add Font Awesome icons to library
library.add(
  faCaretDown,
  faChevronUp,
  faChevronDown,
  faChevronRight,
  faCheck,
  faCheckCircle,
  faTimes,
  faTimesCircle,
  faUserCircle,
  faPlusCircle,
  faExclamationTriangle,
  faExclamationCircle,
  faAngleLeft,
  faCcAmex,
  faCcDinersClub,
  faCcDiscover,
  faCcJcb,
  faCcMastercard,
  faCcVisa,
  faInfoCircle,
  faQuestionCircle
);

// register font-awesome-icon component + spinner
Vue.component("font-awesome-icon", FontAwesomeIcon);
Vue.component("spring-spinner", SpringSpinner);

Vue.config.productionTip = false;

// load various libraries
Vue.use(VTooltip);
Vue.use(Vuex);
Vue.use(VueClipboard);
Vue.use(VueRouter);
Vue.use(Vuelidate);
Vue.use(VModal, {
  dynamic: true,
  dynamicDefaults: {
    adaptive: true,
    width: 725,
    height: "auto",
  },
});
Vue.use(VueTheMask);
Vue.use(VueCurrencyFilter, {
  symbol: "$",
  thousandsSeparator: ",",
  fractionCount: "2",
  fractionSeparator: ".",
  symbolPosition: "front",
  symbolSpacing: false,
});
Vue.use(VueWait);

/** create global vue filters */
/** capitalizes the first letter of each word */
Vue.filter("capitalizeFirstLetter", function(value) {
  if (!value) {
    return "";
  }
  value = value.toString();
  return value.charAt(0).toUpperCase() + value.slice(1);
});
/** returns a string based on a SQL datetime value */
Vue.filter("date", function(value) {
  if (!value || typeof value !== "string") {
    return "";
  }

  /** if timestamp with the letter T split the timestamp off then split by - */
  const [year, month, day] = value.includes("T")
    ? value.split("T")[0].split("-")
    : value.split("-");

  return `${month}/${day}/${year}`;
});
/** returns a fancy string based on a SQL datetime value */
Vue.filter("fancyDate", function(value) {
  return timestampToDateString(value);
});
/** returns an extra fancy string based on a SQL datetime value */
Vue.filter("extraFancyDate", function(value) {
  return timestampToFancyDate(value);
});
/** adds a comma  for every three digits in a number */
Vue.filter("numberWithCommas", function(value) {
  if (!value) {
    return "0";
  }
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
});
/** adds a comma  for every three digits in a number */
Vue.filter("numberToPercent", function(value) {
  if (!value && value !== 0) {
    return "";
  }
  return Math.round(value * 100) + "%";
});
/** abbreviates number with k for thousands and m for millions */
Vue.filter("abbreviateNumber", function(value) {
  if (!value) {
    return "";
  }
  const number = Number(value);
  if (number / 1000000 > 1) {
    return (number / 1000000).toFixed(1).split(".0")[0] + "m";
  }
  if (number / 1000 > 1) {
    return (number / 1000).toFixed(1).split(".0")[0] + "k";
  }
  return value;
});
/** abbreviates only numbers greater than 999,99 and adds commas */
Vue.filter("abbreviateMillion", function(value) {
  if (!value) {
    return "";
  }
  const number = Number(value);
  if (number / 1000000 > 1) {
    return (number / 1000000).toFixed(1).split(".0")[0] + " million";
  }
  if (number / 1000 > 1) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return value;
});

/**
 * refreshes user's token before navigating to each page
 * only if the user has a token already
 * except when going to a page that's not part of the dashboard
 * gets all data if the timer array is empty because those are not persistent
 * otherwise if they navigate to another website and hit the back button
 * the timers will get cleared out and not re-populate
 */
//
router.beforeEach((to, from, next) => {
  if (
    store.state.session.token &&
    to.name !== "login" &&
    to.name !== "default" &&
    to.name !== "maintenance"
  ) {
    if (
      !store.state.timers.timers ||
      (store.state.timers.timers &&
        Array.isArray(store.state.timers.timers) &&
        store.state.timers.timers.length < 1)
    ) {
      store.dispatch("account/getAllData");
    } else {
      store.dispatch("session/getNewToken");
    }
  }
  next();
});

new Vue({
  router,
  store,
  vuetify,
  render: h => h(App),
  wait: new VueWait({ useVuex: true }),
}).$mount("#app");
